<template>
  <div>
    <cg-loader v-if="loading" />

    <div v-show="!loading" class="add-credit-card-form">
      <div class="hosted-field__input-wrapper">
        <label for="card-number">Credit card number</label>

        <div class="hosted-field__credit-card">
          <div class="hosted-field" id="card-number" :class="{ 'hosted-field--error': ccnHasError }" />

          <img :src="creditCardIcon" alt="">
        </div>

        <div v-if="ccnHasError" class="hosted-field__error-message">
          Card number is required
        </div>
      </div>

      <div class="hosted-field__double-inputs">
        <div class="hosted-field__input-wrapper">
          <label for="expiration-date">Expiry date</label>
          <div class="hosted-field" id="expiration-date" :class="{ 'hosted-field--error': expHasError }" />

          <div v-if="expHasError" class="hosted-field__error-message">
            Expiry date is required
          </div>
        </div>
        <div class="hosted-field__input-wrapper">
          <label for="cvv">Card verification code</label>
          <div class="hosted-field" id="cvv" :class="{ 'hosted-field--error': cvvHasError }" />

          <div v-if="cvvHasError" class="hosted-field__error-message">
            CVC is required
          </div>
        </div>
      </div>

      <cg-checkbox v-if="showSetAsDefault" v-model="saveAsDefault" :disabled="forceSaveAsDefault">
        Set as default
      </cg-checkbox>

      <span>
        Billing address
      </span>

      <billing-address-form
        ref="billingAddress"
        @onBullingAddressUpdate="(val) => billingForm = val"
      />

      <cg-button v-if="showAddCardButton" :disabled="disableSaveButton" @click="sendCard">
        Add card
      </cg-button>
    </div>
  </div>
</template>

<script>
import Api from '@/axios/api'
import PaymentType from './paymentModule/utils/PaymentType'

import { CgButton, CgCheckbox, CgLoader } from '@corporategiftcom/design-system'
import BillingAddressForm from './BillingAddressForm.vue'

export default {
  name: 'AddCreditCardForm',
  components: {
    CgButton,
    CgLoader,
    CgCheckbox,
    BillingAddressForm,
  },
  props: {
    showSetAsDefault: {
      type: Boolean,
      required: false,
      default: true,
    },
    showAddCardButton: {
      type: Boolean,
      required: false,
      default: true,
    },
    forceSaveAsDefault: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    creditCardIcon: '/images/credit-cards/master-card.svg',
    loading: false,
    creditCardData: {
      token: null,
      binNumber: null,
    },
    billingForm: {},
    saveAsDefault: false,
    validFlags: {
      ccn: false,
      exp: false,
      cvv: false,
    },
    touchedFields: {
      ccn: false,
      exp: false,
      cvv: false,
    },
    showCardErrors: false,
    braintreeInstance: null,
  }),
  computed: {
    isCCValid () {
      const { validFlags } = this;
      return Object.values(validFlags).every((flag) => flag === true);
    },
    ccnHasError () {
      const { showCardErrors, touchedFields, validFlags } = this
      return (showCardErrors || touchedFields.ccn) && !validFlags.ccn
    },
    expHasError () {
      const { showCardErrors, touchedFields, validFlags } = this
      return (showCardErrors || touchedFields.exp) && !validFlags.exp
    },
    cvvHasError () {
      const { showCardErrors, touchedFields, validFlags } = this
      return (showCardErrors || touchedFields.cvv) && !validFlags.cvv
    },
    disableSaveButton () {
      return !this.isCCValid
    },
  },
  watch: {
    forceSaveAsDefault: {
      immediate: true,
      handler: function (val) {
        this.saveAsDefault = val
      }
    },
    creditCardData: {
      deep: true,
      handler: 'sendPaymentData',
    },
    billingForm: {
      deep: true,
      handler: 'sendPaymentData',
    },
  },
  created () {
    this.initBraintree();
  },
  methods: {
    sendPaymentData () {
      const creditCardData = this.getCreditCardData()
      this.$emit('update:paymentData', creditCardData)
    },
    // public
    refreshPaymentToken () {
      const { braintreeInstance } = this
      if (!braintreeInstance) { throw new Error('Missing payment gateway instance') }

      braintreeInstance.tokenize((err, payload) => {
        if (err) { return }
        this.creditCardData = {
          token: payload?.nonce ?? null,
          binNumber: payload?.details?.bin ?? null,
        }
      });
    },
    initBraintree () {
      this.loading = true

      Api.get('/braintree/token')
        .then(({ token }) => {
          const createHostedFields = (clientInstance) => {
            window.braintree.hostedFields.create({
              client: clientInstance,
              styles: {
                'input': {
                  'font-size': '15px',
                  'background-color': 'white',
                  'color': '#000',
                  'padding': '0 16px',
                  'font-family': 'Lato-Regular, sans-serif',
                },
                ':focus': {
                  'color': '#000'
                },
                '::placeholder': {
                  'font-style': 'italic',
                  color: '#919191',
                  'font-family': 'Lato-Italic, sans-serif',
                },
                '.invalid': {
                  'color': '#FF5A60',
                }
              },
              fields: {
                number: {
                  selector: '#card-number',
                  placeholder: '____ ____ ____ ____'
                },
                cvv: {
                  selector: '#cvv',
                  placeholder: 'CVC'
                },
                expirationDate: {
                  selector: '#expiration-date',
                  placeholder: 'MM/YY'
                }
              }
            }, (err, hostedFieldsInstance) => {
              this.loading = false

              if (err) {
                console.error(err)
                return
              }

              this.braintreeInstance = hostedFieldsInstance ?? null

              hostedFieldsInstance.on('validityChange', (event) => {
                this.validFlags = {
                  ccn: event.fields.number.isValid,
                  exp: event.fields.expirationDate.isValid,
                  cvv: event.fields.cvv.isValid,
                }

                hostedFieldsInstance.setAttribute({
                  field: 'number',
                  attribute: 'aria-invalid',
                  value: event.fields.number.isValid ? 'false' : 'true',
                })

                hostedFieldsInstance.setAttribute({
                  field: 'expirationDate',
                  attribute: 'aria-invalid',
                  value: event.fields.expirationDate.isValid ? 'false' : 'true',
                })

                hostedFieldsInstance.setAttribute({
                  field: 'cvv',
                  attribute: 'aria-invalid',
                  value: event.fields.cvv.isValid ? 'false' : 'true',
                })

                if (event.fields.cvv.isValid) {
                  hostedFieldsInstance.removeClass('cvv', 'invalid')
                } else {
                  hostedFieldsInstance.addClass('cvv', 'invalid')
                }

                if (this.isCCValid) {
                  this.updateCreditCardIcon(event.cards[0].type)
                  this.refreshPaymentToken()
                }
              });

              hostedFieldsInstance.on('blur', (event) => {
                const fieldKeys = { number: 'ccn', expirationDate: 'exp', cvv: 'cvv' }
                const key = fieldKeys[event.emittedBy]

                if (key) { this.touchedFields[key] = true }
              });
            });
          }

          window.braintree.client.create(
            { authorization: token },
            (err, clientInstance) => {
              if (err) {
                console.error(err);
                return;
              }

              createHostedFields(clientInstance)

              window.braintree.dataCollector.create(
                { client: clientInstance, kount: true },
                (error, dataCollectorInstance) => {
                  if (!error) {
                    sessionStorage.setItem('braintreeDeviceData', dataCollectorInstance?.deviceData)
                  }
                }
              );
            }
          );
        })
        .catch(() => {
          console.error('Fetch client braintree token issue')
        })
    },
    updateCreditCardIcon (cardType) {
      if (cardType) {
        this.creditCardIcon = `/images/credit-cards/${cardType.toLowerCase()}.svg`
      }
    },
    sendCard () {
      if (!this.validate()) { return }

      this.loading = true
      const creditCardData = this.getCreditCardData()
      const gRecaptcha = window.grecaptcha.enterprise

      gRecaptcha.ready(() => {
        gRecaptcha.execute(process.env.VUE_APP_RECAPTCHA_PUBLIC_KEY, { action: 'SUBMIT' })
          .then((token) => {
            Api.post('braintree/payment-methods', { ...creditCardData, recaptcha_token: token })
              .then(() => (this.$emit('onAddCard')))
              .catch(({ response }) => {
                const errorMessage = response?.data?.message || response?.data || 'An error occurred, please contact our support'
                this.$cgToast.error(errorMessage, { html: true })
                this.refreshPaymentToken();
              })
              .finally(() => (this.loading = false))
          })
          .catch(() => (this.loading = false))
      })
    },
    // public
    validate () {
      const { isCCValid } = this
      const isBillingAddressValid = this.$refs.billingAddress?.validate()

      this.showCardErrors = !isCCValid

      return isCCValid && isBillingAddressValid;
    },
    // public
    getCreditCardData () {
      const { creditCardData, billingForm, saveAsDefault } = this;

      return {
        payment: {
          paymentMethodNonce: creditCardData?.token,
          method: PaymentType.CC,
        },
        isDefault: saveAsDefault,
        billingAddress: { ...billingForm },
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.add-credit-card-form {
  display: flex;
  flex-direction: column;
  gap: 30px;

  & > span {
    padding: 20px 0 10px;
    color: #95979D;
    text-transform: uppercase;
    letter-spacing: 2.5px;
    line-height: normal;
    font-family: 'Lato-Bold', sans-serif;
    font-size: 14px;
  }

  & button {
    border-radius: 100px;
  }
}

.cg-loader {
  max-height: 200px;
  margin: 200px auto 0;
}
</style>

<style lang="scss">
.hosted-field {
  &__input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;

    & label {
      color: #000;
      font-family: 'Lato-Bold', sans-serif;
      font-size: 15px;
      line-height: normal;
    }
  }

  &__double-inputs {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 16px;
  }

  &__credit-card {
    position: relative;

    & > img {
      width: 40px;
      object-fit: contain;
      position: absolute;
      right: 8px;
      top: 50%;
      transform: translateY(-50%);
    }
  }

  border-radius: 2px;
  transition: background-color ease-in-out 0.18s;

  & iframe {
    display: block;
    width: 100%;
    max-height: 40px;
    border: 1px solid #d3d2d2 !important;
  }

  &--error {
    background: #FBE7E9;

    & iframe {
      border-color: #FF5A60 !important;
    }
  }

  &__error-message {
    font-family: 'Lato', sans-serif;
    font-style: italic;
    font-weight: 400;
    font-size: 14px;
    letter-spacing: 0.186667px;
    color: #FF5A60;
    text-align: left;
  }
}
</style>
